import _ from "lodash";
import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import { fetchPoliciesB2C } from "../../../../../api/CRM/PoliciesB2C/PoliciesB2C";
import Loader from "../../../../../components/Loader";
import {
  genericIndexesStyles,
  tableStyles,
  variables,
} from "../../../../../variables";
import Navbar from "./Navbar";
import ControlStrip from "../../../../../components/ControlStrip";
import { medWeekdayDatetimer, shortDater } from "../../../../../helpers";
import DetailB2CModal from "../DetailB2CModal";
import HamburgerMenu from "../../../../../components/HamburgerMenu";
import { useListPageSidebar } from "../../../../../hooks";
import * as yup from "yup";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { genericRequestErrorHandler } from "../../../../../api";
import { toast } from "react-toastify";
import { CustomButton } from "../../../../../components/CustomButton";

export const policiesStatusMapping = {
  AC: "Active",
  IN: "Inactive",
  CA: "Candidate",
};

const initialFilters = {
  limit: 20,
  page: 1,
  status: "",
  name__icontains: "",
  booking_window_from__gte: "",
  booking_window_to__lte: "",
  service_execution_to__lte: "",
  service_execution_from__gte: "",
  ordering: "",
  discending: "",
  created__gte: "",
  created__lte: "",
  source_entity: "",
};

const navbarSchema = yup.object().shape({
  name__icontains: yup
    .string()
    .min(2, "Policy Name must be more than 2 characters")
    .max(100, "Policy Name must be less than 100 characters"),
  // created__gte: yup.date().max(new Date(), "Date cannot be in the future"),
  // created__lte: yup.date().max(new Date(), "Date cannot be in the future"),
});

const PolicieB2CStyles = createUseStyles({
  PolicieB2C: genericIndexesStyles.indexes.mobile(),
  tableContainer: genericIndexesStyles.tableContainer.mobile(),
  table: tableStyles.table,
  thead: tableStyles.head,
  headRow: tableStyles.head.row,
  headCell: tableStyles.head.cell,
  bodyRow: tableStyles.body.row,
  tableCell: tableStyles.body.cell,
  actionCell: {
    ...tableStyles.body.cell,
    gap: variables.half_gap,
  },
  paxCell: { ...tableStyles.body.cell, width: "4rem", minWidth: "4rem" },
  distributionPeriodCell: { ...tableStyles.body.cell, width: "12rem" },
  reservationPeriodCell: { ...tableStyles.body.cell, width: "8rem" },
  roundingDigitsCell: { ...tableStyles.body.cell, width: "0.1rem" },
  datesCell: { ...tableStyles.body.cell, width: "13rem" },
  [`@media ${variables.media.smallscreen}`]: {
    actionCell: {
      gridTemplateColumns: "repeat(2,1fr) ",
      paddingTop: `${variables.normal_gap}!important`,
    },
    tableCell: {
      padding: `calc(${variables.normal_gap} * 2.5) 0`,
    },
  },
});

const PoliciesB2C = () => {
  const classes = PolicieB2CStyles();

  useListPageSidebar();

  const [filters, setFilters] = useState(initialFilters);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [B2CId, setB2CId] = useState(null);

  const tableHeaders = {
    "Policy Name": (val) => _.capitalize(_.get(val, "name")),
    "Source Entity": (val) => `${val.member}___member`,
    Pax: (val) =>
      `${val.pax_from ? val.pax_from : "N/A"} to ${
        val.pax_to ? val.pax_to : "N/A"
      }`,
    // "B2C Websites": (val) =>
    //   val.b2c_websites
    //     ? val.b2c_websites
    //         .map(
    //           (wbs) =>
    //             `${_.get(_.get(websites, wbs, {}), "name") || "N/A"}(${
    //               _.get(_.get(websites, wbs, {}), "status") || "N/A"
    //             })`
    //         )
    //         .join(",")
    //     : "N/A",
    "Distribution Period": (val) => (
      <React.Fragment>
        <span>
          {val.booking_window_from
            ? shortDater(val.booking_window_from)
            : "N/A"}
        </span>
        <br />
        <span>-</span>
        <br />
        <span>
          {val.booking_window_to ? shortDater(val.booking_window_to) : "N/A"}
        </span>
      </React.Fragment>
    ),
    "Reservation Period": (val) => (
      <React.Fragment>
        <span>
          {val.service_execution_from
            ? shortDater(val.service_execution_from)
            : "N/A"}
        </span>
        <br />
        <span>-</span>
        <br />
        <span>
          {val.service_execution_to
            ? shortDater(val.service_execution_to)
            : "N/A"}
        </span>
      </React.Fragment>
    ),
    "Rounding Digits": (val) => val.rounding_digits || "N/A",
    Created: (val) => val.created,
    Status: (val) =>
      _.get(policiesStatusMapping, _.get(val, "status")) || "N/A",
  };

  const handleApplyFilters = async (filters) => {
    if (filters.source_entity !== "") {
      try {
        const filteredFilters = Object.keys(filters).reduce((acc, key) => {
          if (filters[key] !== "") {
            acc[key] = filters[key];
          }
          return acc;
        }, {});

        await navbarSchema.validate(filteredFilters, { abortEarly: false });
        setFilters(filteredFilters);
      } catch (error) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          if (!validationErrors[err.path]) {
            validationErrors[err.path] = err.message;
            toast.error(err.message);
          }
        });
      }
    } else {
      toast.error(`You must choose Source Entity`);
    }
  };
  const { data: policiesB2C, isLoading } = useQuery({
    queryKey: ["PoliciesB2C", filters],
    queryFn: () => {
      const memberId = _.get(filters, "source_entity", []).split("___")[0];
      let correctPayload = { ...filters, source_entity_id: memberId };
      delete correctPayload["source_entity"];
      if (filters.created__lte) {
        correctPayload["created__lte"] = DateTime.fromISO(
          filters.created__lte
        ).toISO();
      }
      if (filters.created__gte) {
        correctPayload["created__gte"] = DateTime.fromISO(
          filters.created__gte
        ).toISO();
      }
      return fetchPoliciesB2C({
        params: correctPayload,
      });
    },
    enabled: !showDetailModal,
    onError: (error) => genericRequestErrorHandler(error),
  });

  return (
    <div className={classes.PolicieB2C}>
      <Navbar
        filters={filters}
        initialFilters={initialFilters}
        onApplyFilters={handleApplyFilters}
      />
      <HamburgerMenu />
      <ControlStrip
        limit={filters.limit}
        page={filters.page}
        count={_.get(policiesB2C, "data.count", 0)}
        onChangeLimit={(limit) => setFilters({ ...filters, limit, page: 1 })}
        onChangePage={(page) => setFilters({ ...filters, page })}
        orderOptions={[["created", "Created"]]}
        onChangeOrderOptions={(ordering) =>
          setFilters({ ...filters, ordering })
        }
      />
      {isLoading && <Loader onTop={true} />}
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr className={classes.headRow}>
              <th className={classes.headCell}>#</th>
              {Object.keys(tableHeaders).map((value, idx) => (
                <th className={classes.headCell} key={idx}>
                  {value}
                </th>
              ))}
              <th className={classes.headCell}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {_.get(policiesB2C, "data.results", []).map((val, idx) => (
              <tr className={classes.bodyRow} key={idx}>
                <td className={classes.tableCell}>{idx + 1}</td>
                {Object.entries(tableHeaders).map((entry, ridx) => {
                  var cell = null;
                  const fn = entry[1];
                  switch (entry[0].toLowerCase()) {
                    case "last edited":
                      cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                      return (
                        <td className={classes.datesCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "created":
                      cell = fn(val) ? medWeekdayDatetimer(fn(val)) : "N/A";
                      return (
                        <td className={classes.datesCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "rounding digits":
                      cell = fn(val) || "N/A";
                      return (
                        <td className={classes.roundingDigitsCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    case "reservation period":
                      cell = fn(val) || "N/A";
                      return (
                        <td
                          className={classes.reservationPeriodCell}
                          key={ridx}>
                          {cell}
                        </td>
                      );
                    case "distribution period":
                      cell = fn(val) || "N/A";
                      return (
                        <td
                          className={classes.distributionPeriodCell}
                          key={ridx}>
                          {cell}
                        </td>
                      );
                    case "pax":
                      cell = fn(val) || "N/A";
                      return (
                        <td className={classes.paxCell} key={ridx}>
                          {cell}
                        </td>
                      );
                    default:
                      cell = fn(val) || "N/A";
                      break;
                  }
                  return (
                    <td className={classes.tableCell} key={ridx}>
                      {cell}
                    </td>
                  );
                })}
                <td className={classes.actionCell}>
                  <CustomButton
                    appearance="ghost"
                    onClick={() => {
                      setB2CId(val.id);
                      setShowDetailModal(true);
                    }}>
                    <strong>Details</strong>
                  </CustomButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showDetailModal && (
        <DetailB2CModal
          B2CId={B2CId}
          onClose={() => {
            setShowDetailModal(false);
          }}
        />
      )}
    </div>
  );
};

export default PoliciesB2C;
